import type { Media } from '@wineryxplorer/shared';

interface ImageLoadState {
  loaded: boolean;
  loading: boolean;
  error: boolean;
  url?: string;
  blurUrl?: string;
  errorMessage?: string;
  retryCount?: number;
}

interface PaginationDotsProps {
  images: Media[];
  currentIndex: number;
  imageStates: Map<string, ImageLoadState>;
  isTransitioning: boolean;
  onGoToSlide: (index: number) => void;
}

export const PaginationDots = ({
  images,
  currentIndex,
  imageStates,
  isTransitioning,
  onGoToSlide,
}: PaginationDotsProps) => {
  return (
    <div 
      className="absolute bottom-4 left-1/2 -translate-x-1/2 flex"
      role="tablist"
      aria-label="Carousel navigation"
    >
      {images.map((image, index) => {
        const imageState = imageStates.get(image.id);
        
        // Don't show dots for failed images
        if (imageState?.error) return null;
        
        return (
          <button
            key={index}
            onClick={() => onGoToSlide(index)}
            disabled={isTransitioning || (imageState?.loading)}
            className={`flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 touch-manipulation ${
              isTransitioning || imageState?.loading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            }`}
            style={{ width: '24px', height: '24px' }} // 24px touch target for max spacing
            role="tab"
            aria-selected={index === currentIndex}
            aria-controls={`carousel-image-${index}`}
            aria-label={`Go to image ${index + 1} of ${images.length}${imageState?.loading ? ' (loading)' : ''}`}
            title={`Image ${index + 1}`}
          >
            <div className={`w-1 h-1 rounded-full transition-all duration-200 ${
              index === currentIndex
                ? 'bg-white scale-150'
                : 'bg-white/50 hover:bg-white/70'
            }`}>
              {/* Loading indicator for dots */}
              {imageState && imageState.loading && (
                <div className="w-full h-full rounded-full border border-white/30 border-t-white animate-spin" />
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};